import React from 'react';
import { EducationFormatter } from '@/utils/educationFormatter';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template4Props {
  data: ResumeData;
}

export const Template4: React.FC<Template4Props> = ({ data }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white text-gray-900 font-sans">
      {/* Clean Header */}
      <header className="text-center py-8 mb-8 border-b-4 border-emerald-500">
        <h1 className="text-4xl font-light text-gray-900 mb-2">{data.personalInfo.name}</h1>
        <h2 className="text-xl text-emerald-600 font-medium mb-6">{data.personalInfo.title}</h2>
        
        <div className="flex justify-center space-x-8 text-sm text-gray-600">
          <span>{data.personalInfo.email}</span>
          <span>{data.personalInfo.phone}</span>
          <span>{data.personalInfo.location}</span>
          {data.personalInfo.linkedin && <span>{data.personalInfo.linkedin}</span>}
          {data.personalInfo.website && <span>{data.personalInfo.website}</span>}
        </div>
      </header>

      <div className="px-8">
        {/* Summary in box */}
        <section className="mb-8">
          <div className="border-l-4 border-emerald-500 pl-6 py-4 bg-emerald-50">
            <h3 className="text-lg font-semibold text-emerald-700 mb-3">PROFESSIONAL SUMMARY</h3>
            <p className="text-gray-700 leading-relaxed">{data.summary}</p>
          </div>
        </section>

        {/* Experience */}
        <section className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 pb-2 border-b-2 border-emerald-500">
            PROFESSIONAL EXPERIENCE
          </h3>
          
          {data.experience.map((job, index) => (
            <div key={index} className="mb-8">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{job.title}</h4>
                    <p className="text-emerald-600 font-medium text-lg">{job.company}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-700">{job.dates}</p>
                    <p className="text-sm text-gray-600">{job.location}</p>
                  </div>
                </div>
              </div>
              
              <ul className="space-y-2 ml-4">
                {job.bullets.map((bullet, bulletIndex) => (
                  <li key={bulletIndex} className="flex items-start">
                    <span className="text-emerald-500 mr-3 mt-1">▶</span>
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {/* Education and Skills Side by Side */}
        <div className="grid grid-cols-2 gap-8">
          {/* Education */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-500">
              EDUCATION
            </h3>
            
            <EducationFormatter 
              education={data.education} 
              className="text-gray-900"
            />
          </section>

          {/* Skills */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-500">
              CORE COMPETENCIES
            </h3>
            
            <div className="space-y-2">
              {data.skills.map((skill, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                  <span className="text-gray-700">{skill}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};